import type { PerformanceReportRow } from "@/types/reporting";

export const GEO_TOP_N = 10;
export const MIN_IMPRESSIONS_FOR_CTR_SIGNIFICANCE = 1000;

export interface RegionData {
  region_name: string;
  impressions: number;
  clicks: number;
  ctr: number;
  video_complete_rate: number;
}

export interface CityData {
  city_name: string;
  region_name: string;
  impressions: number;
  clicks: number;
  // null when the city has zero clicks — excluded from "top CTR" ranking
  ctr: number | null;
}

interface TopMarketCards {
  topImpressionsCity: CityData | null;
  topCtrCity: CityData | null;
}

// Strips the "usa-xx-" slug prefix returned by the API and title-cases the remainder.
// e.g. "usa-ca-los angeles" → "Los Angeles", "usa-sd-sioux falls" → "Sioux Falls".
// Idempotent on strings that don't carry the prefix.
const normalizeCity = (raw: string): string =>
  raw.replace(/^usa-[a-z]{2}-/, "").replace(/\b\w/g, (c) => c.toUpperCase());

const STATE_ABBREVS: Record<string, string> = {
  Alabama: "AL",
  Alaska: "AK",
  Arizona: "AZ",
  Arkansas: "AR",
  California: "CA",
  Colorado: "CO",
  Connecticut: "CT",
  Delaware: "DE",
  "District of Columbia": "DC",
  Florida: "FL",
  Georgia: "GA",
  Hawaii: "HI",
  Idaho: "ID",
  Illinois: "IL",
  Indiana: "IN",
  Iowa: "IA",
  Kansas: "KS",
  Kentucky: "KY",
  Louisiana: "LA",
  Maine: "ME",
  Maryland: "MD",
  Massachusetts: "MA",
  Michigan: "MI",
  Minnesota: "MN",
  Mississippi: "MS",
  Missouri: "MO",
  Montana: "MT",
  Nebraska: "NE",
  Nevada: "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  Ohio: "OH",
  Oklahoma: "OK",
  Oregon: "OR",
  Pennsylvania: "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  Tennessee: "TN",
  Texas: "TX",
  Utah: "UT",
  Vermont: "VT",
  Virginia: "VA",
  Washington: "WA",
  "West Virginia": "WV",
  Wisconsin: "WI",
  Wyoming: "WY",
};

const CANONICAL_REGIONS = new Map(
  Object.keys(STATE_ABBREVS).map((name) => [name.toLowerCase(), name])
);

// The API sends region names lowercase ("north carolina"), but the map's shapes
// are named in title case and ECharts matches names case-sensitively, so an
// unnormalized name leaves every state blank. Known states resolve to their
// canonical spelling (which keeps "District of Columbia" right); anything else
// falls back to title case. Idempotent on names that are already title case.
export const normalizeRegion = (raw: string): string => {
  const trimmed = raw.trim();
  return (
    CANONICAL_REGIONS.get(trimmed.toLowerCase()) ??
    trimmed.replace(/\b\w/g, (char) => char.toUpperCase())
  );
};

export const cityLabel = (
  city: Pick<CityData, "city_name" | "region_name">
): string => {
  const abbrev = STATE_ABBREVS[city.region_name];
  return abbrev ? `${city.city_name}, ${abbrev}` : city.city_name;
};

// Aggregates per-postal-code API rows into one CityData per (city, region) pair.
// The API groups at ZIP-code granularity regardless of the fields requested, so
// client-side aggregation is required to avoid duplicate cities in the bar chart.
export const collectCities = (rows: PerformanceReportRow[]): CityData[] => {
  const cityMap = new Map<
    string,
    {
      city_name: string;
      region_name: string;
      impressions: number;
      clicks: number;
    }
  >();

  rows
    .filter((row) => Boolean(row.city_name) && Boolean(row.region_name))
    .forEach((row) => {
      const regionName = normalizeRegion(row.region_name!);
      const key = `${regionName}|${row.city_name}`;
      const current = cityMap.get(key) ?? {
        city_name: normalizeCity(row.city_name!),
        region_name: regionName,
        impressions: 0,
        clicks: 0,
      };
      cityMap.set(key, {
        ...current,
        impressions: current.impressions + (row.impressions ?? 0),
        clicks: current.clicks + (row.clicks ?? 0),
      });
    });

  return Array.from(cityMap.values()).map(
    ({
      city_name: cityName,
      region_name: regionName,
      impressions,
      clicks,
    }) => ({
      city_name: cityName,
      region_name: regionName,
      impressions,
      clicks,
      ctr: clicks > 0 && impressions > 0 ? (clicks / impressions) * 100 : null,
    })
  );
};

export const deriveRegionTotals = (
  rows: PerformanceReportRow[]
): RegionData[] => {
  const regionMap = new Map<
    string,
    {
      impressions: number;
      clicks: number;
      weightedVcr: number;
      vcrImpressions: number;
    }
  >();

  rows
    .filter((row) => Boolean(row.region_name))
    .forEach((row) => {
      const region = normalizeRegion(row.region_name as string);
      const impressions = row.impressions ?? 0;
      const current = regionMap.get(region) ?? {
        impressions: 0,
        clicks: 0,
        weightedVcr: 0,
        vcrImpressions: 0,
      };
      // Impression-weighted average: exact when video_complete_rate = completions/impressions.
      // If the API uses completions/video_starts as the denominator, this is an approximation,
      // but the error is negligible within a state where start-rate varies little across ZIPs.
      // Rows with null VCR are excluded from both the numerator and the VCR denominator so
      // they don't dilute regions that mix video and non-video placements.
      const rawVcr = row.video_complete_rate;
      regionMap.set(region, {
        impressions: current.impressions + impressions,
        clicks: current.clicks + (row.clicks ?? 0),
        weightedVcr:
          current.weightedVcr + (rawVcr != null ? impressions * rawVcr : 0),
        vcrImpressions:
          current.vcrImpressions + (rawVcr != null ? impressions : 0),
      });
    });

  return Array.from(regionMap.entries()).map(
    ([region, { impressions, clicks, weightedVcr, vcrImpressions }]) => {
      const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
      const videoCompleteRate =
        vcrImpressions > 0 ? weightedVcr / vcrImpressions : 0;
      return {
        region_name: region,
        impressions,
        clicks,
        ctr,
        video_complete_rate: videoCompleteRate,
      };
    }
  );
};

export type RegionCities = Map<
  string,
  Array<{ city_name: string; impressions: number }>
>;

export const deriveRegionCities = (
  rows: PerformanceReportRow[]
): RegionCities => {
  const regionMap = new Map<string, Map<string, number>>();

  rows
    .filter((row) => Boolean(row.region_name) && Boolean(row.city_name))
    .forEach((row) => {
      const regionName = normalizeRegion(row.region_name!);
      const cityMap = regionMap.get(regionName) ?? new Map<string, number>();
      cityMap.set(
        row.city_name!,
        (cityMap.get(row.city_name!) ?? 0) + (row.impressions ?? 0)
      );
      regionMap.set(regionName, cityMap);
    });

  const result: RegionCities = new Map();
  regionMap.forEach((cityMap, region) => {
    result.set(
      region,
      Array.from(cityMap.entries())
        .map(([cityName, impressions]) => ({
          city_name: normalizeCity(cityName),
          impressions,
        }))
        .sort((a, b) => b.impressions - a.impressions)
    );
  });
  return result;
};

export const deriveTopCities = (
  cities: CityData[],
  topN: number = GEO_TOP_N
): CityData[] =>
  [...cities].sort((a, b) => b.impressions - a.impressions).slice(0, topN);

export const deriveTopMarketCards = (cities: CityData[]): TopMarketCards => {
  if (cities.length === 0) {
    return { topImpressionsCity: null, topCtrCity: null };
  }

  const topByImpressions = cities.reduce((max, city) =>
    city.impressions > max.impressions ? city : max
  );

  const eligible = cities.filter(
    (city) =>
      city.impressions >= MIN_IMPRESSIONS_FOR_CTR_SIGNIFICANCE &&
      city.ctr !== null
  );
  const topByCtr =
    eligible.length > 0
      ? eligible.reduce((best, city) => (city.ctr! > best.ctr! ? city : best))
      : null;

  return {
    topImpressionsCity: topByImpressions,
    topCtrCity: topByCtr,
  };
};
